import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table,
  Toast,
  ToastBody,
  ToastHeader,
} from "reactstrap";
import {
  getMaintenanceCategory,
  getMaintenanceCategoryById,
  updateMaintenanceCategory,
  deleteMaintenanceCategory,
  getAllUsersList,
} from "../../../slices/thunks";
import { setLoading } from "../../../slices/maintenanceCategory/reducer";
import DeleteModal from "../../../Components/Common/DeleteModal";
import { Pagination } from "../../../Components/Common/Pagination";
import { TripModal } from "../../../Components/Common/TripModal/tripModal";
import { Formik } from "formik";
import moment from "moment/moment";
import * as Yup from "yup";

export default function MaintenanceTableCategory() {
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [addCategoryModal, setAddCategoryModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toasterType, setToasterType] = useState("");
  const [deleteUserModal, setDeleteUserModal] = useState(false);

  const loading = useSelector((state) => state.MaintenanceCategory.loading);
  const categoryList = useSelector(
    (state) => state.MaintenanceCategory.maintenanceCategoryList
  );
  const categoryById = useSelector(
    (state) => state.MaintenanceCategory.maintenanceCategoryById
  );
  const usersList = useSelector((state) => state.UsersManagement.usersList);

  const toggleAddCategoryModal = () => setAddCategoryModal(!addCategoryModal);
  const toggleDeleteUserModal = () => setDeleteUserModal(!deleteUserModal);

  const validationSchemaForCategory = Yup.object().shape({
    name: Yup.string()
      .transform((value) => value.trim())
      .required("Category name is required")
      .test(
        "not-only-spaces",
        "Category name cannot be only spaces",
        (value) => !!value && value.trim().length > 0
      ),
  });

  const updateInitialValues = {
    id: categoryById?.id || "",
    name: categoryById?.name || "",
  };

  const showToaster = (isSuccess, message) => {
    setShowToast(true);
    setToasterType(isSuccess ? "success" : "danger");
    setToastMessage(message);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleCategorySubmit = async (values) => {
    try {
      const response = await dispatch(
        updateMaintenanceCategory(categoryById?.id, values)
      );
      const { success: isSuccess, message } = response;
      showToaster(isSuccess, message || "Operation successful");
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while processing your request"
      );
    } finally {
      toggleAddCategoryModal();
      await dispatch(getMaintenanceCategory(currentPage, pageSize, searchText));
    }
  };

  const handleDeleteUser = async () => {
    try {
      const { success: isSuccess, message } = await dispatch(
        deleteMaintenanceCategory(categoryById?.id)
      );
      if (isSuccess) showToaster(isSuccess, message || "Deleted Successfully");
    } catch (error) {
      showToaster(false, error?.message || "An error occurred while deleting");
    } finally {
      toggleDeleteUserModal();
      await dispatch(getMaintenanceCategory(currentPage, pageSize, searchText));
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      try {
        dispatch(getAllUsersList("", "", ""));
        dispatch(getMaintenanceCategory(currentPage, pageSize, searchText));
      } catch (error) {}
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [dispatch, currentPage, pageSize, searchText]);

  const getCreatedByName = (userId) => {
    if (!userId) return "Unknown";
    const usersArray = Array.isArray(usersList) ? usersList : [];
    const user = usersArray.find((user) => String(user.id) === String(userId));
    return user
      ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
      : "Unknown";
  };

  return (
    <>
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1055 }}>
        {showToast && (
          <Toast isOpen={showToast}>
            <ToastHeader
              icon={toasterType === "danger" ? "danger" : "success"}
              toggle={() => setShowToast(false)}
            >
              {toasterType === "danger" ? "Error" : "Success"}
            </ToastHeader>
            <ToastBody>{toastMessage}</ToastBody>
          </Toast>
        )}
      </div>

      <div className="trip-table">
        <Table hover>
          <thead>
            <tr>
              <th>Sr.No</th>
              <th>Maintenance Type</th>
              <th>Created By</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Action</th>
            </tr>
          </thead>

          {loading.read ? (
            <tbody>
              <tr>
                <td colSpan="9" className="text-center align-middle">
                  <Spinner color="primary" />
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {categoryList?.results?.length ? (
                categoryList.results.map((element, index) => (
                  <tr key={index}>
                    <td>{(currentPage - 1) * pageSize + index + 1}</td>
                    <td>{element.name}</td>
                    <td>{getCreatedByName(element.created_by)}</td>
                    <td>{moment(element?.created_at).format("D MMM YYYY")}</td>
                    <td>{moment(element?.updated_at).format("D MMM YYYY")}</td>
                    <td>
                      <div className="action-icons">
                        <i
                          className="ri-pencil-fill icon"
                          style={{ color: "#007AFF" }}
                          onClick={() => {
                            dispatch(getMaintenanceCategoryById(element.id));
                            toggleAddCategoryModal();
                            dispatch(
                              setLoading({ type: "read", value: false })
                            );
                          }}
                        />
                        <i
                          className="ri-delete-bin-fill icon"
                          style={{ color: "#FB0000" }}
                          onClick={() => {
                            dispatch(getMaintenanceCategoryById(element.id));
                            toggleDeleteUserModal();
                            dispatch(
                              setLoading({ type: "read", value: false })
                            );
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="text-start text-sm-center" colSpan={9}>
                    <h3>No Maintenance Category Found</h3>
                  </td>
                </tr>
              )}
            </tbody>
          )}
        </Table>
        {categoryList?.results?.length > 0 && (
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalItems={categoryList?.count || 0}
          />
        )}
      </div>

      <TripModal
        isModalOpen={addCategoryModal}
        toggleModal={toggleAddCategoryModal}
        buttonText={"Update"}
        closeText={"Close without saving"}
        title={"Category Management"}
        tripText={"Update Category "}
        showcloseIcon={true}
        size="lg"
        loading={loading.update}
        onSubmit={() => {
          if (formikRef.current) {
            formikRef.current.handleSubmit();
          }
        }}
      >
        <div className="px-3 gap-4">
          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={updateInitialValues}
            validationSchema={validationSchemaForCategory}
            onSubmit={handleCategorySubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="name" className="passwordLabel">
                        Name
                      </Label>
                      <Input
                        type="text"
                        name="name"
                        className="profileInput"
                        id="name"
                        placeholder="Enter Category Name"
                        value={values.name}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        invalid={touched.name && !!errors.name}
                      />
                      {touched.name && errors.name && (
                        <FormFeedback type="invalid">
                          {errors.name}
                        </FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </div>
      </TripModal>
      <DeleteModal
        show={deleteUserModal}
        loading={loading.delete}
        onDeleteClick={() => {
          handleDeleteUser();
        }}
        onCloseClick={toggleDeleteUserModal}
      />
    </>
  );
}
